import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";

import { createTimelineOverride } from "../../createTimelineOverride.js";
import type {
  LenderFeePaymentSchedule,
  ShapeLenderBase,
  ShapeLenderFee,
} from "../../shapes/ShapeLender.js";
import { WithNameDelegate } from "../delegates/WithNameDelegate.js";
import { WithProjectionPeriodsDelegate } from "../delegates/WithProjectionPeriodsDelegate.js";
import type { NominatorProvidersByKey } from "../RelativesSetFacade.js";
import { relativesSetFacadeWithAbsolute } from "../RelativesSetFacade.js";

export const createLenderFee = (
  name: string,
  schedule: LenderFeePaymentSchedule,
): ShapeLenderFee => ({
  name,
  relatives: {
    absolute: null,
    ratioFacilitySize: null,
    ratioDebtFundedGdv: null,
  },
  paymentSchedule: schedule,
  projectionPeriods: [],
  timelineOverride: createTimelineOverride(),
});

export class LenderFeeFacade {
  readonly $model;

  constructor($model: ShapeLenderFee) {
    makeAutoObservable(this);

    this.$model = $model;
  }

  get paymentSchedule() {
    return this.$model.paymentSchedule;
  }

  get relatives() {
    return this.$model.relatives;
  }

  get named() {
    return new WithNameDelegate(this.$model);
  }

  get projectionPeriods() {
    return new WithProjectionPeriodsDelegate(this.$model);
  }

  set paymentSchedule(schedule: LenderFeePaymentSchedule) {
    this.$model.paymentSchedule = schedule;
  }
}

export type LenderBaseFacadeProviders = NominatorProvidersByKey<
  "debtFundedDevCost" | "debtFundedGdv"
>;

export class LenderBaseFacade {
  constructor(
    readonly $model: ShapeLenderBase,
    readonly _providers: LenderBaseFacadeProviders,
  ) {
    makeAutoObservable(this);
  }

  get name() {
    return this.$model.name;
  }

  get facilitySize() {
    return relativesSetFacadeWithAbsolute(this.$model.facilitySize, {
      ratioDebtFundedDevCost: this._providers.debtFundedDevCost,
      ratioDebtFundedGdv: this._providers.debtFundedGdv,
    });
  }

  readonly _decorateFee = computedFn(
    (fee: ShapeLenderFee) => new LenderFeeFacade(fee),
  );

  get fees() {
    return inferReadonlyArray(
      this.$model.fees.map((fee) => this._decorateFee(fee)),
    );
  }

  set name(name: string) {
    this.$model.name = name;
  }

  addFee() {
    this.$model.fees.push(
      createLenderFee(
        `Lender fee ${this.$model.fees.length + 1}`,
        "commitment",
      ),
    );
  }

  removeFee(fee: LenderFeeFacade) {
    pull(this.$model.fees, fee.$model);
  }
}
